/**
 * The two-pane desktop layout. There are never three panes. Nothing is
 * added: the right pane holds exactly the phone's detail screen.
 *
 * Nesting one of these inside another's detail pane would draw a third column
 * at `lg`, so it throws `#20260819-app338` rather than rendering. The depth
 * lives in a module-private context with no prop opt-out, because a nested
 * two-pane can only ever be a developer mistake.
 */
import { createContext, type ReactNode, useContext } from "react";

const PANES = "lg:flex lg:items-start lg:gap-6";

const SectionTwoPaneDepthContext = createContext(0);

export const SectionTwoPane = ({
  list,
  detail,
  emptyDetail,
}: {
  list: ReactNode;
  /** `null` means nothing is selected. Driven by a URL param, never by state. */
  detail: ReactNode | null;
  emptyDetail: ReactNode;
}): ReactNode => {
  const depth = useContext(SectionTwoPaneDepthContext);
  if (depth !== 0) {
    throw new Error(
      "#20260819-app338 SectionTwoPane cannot be nested inside another SectionTwoPane. A window is never more than two panes wide. Render the nested screen inside the right pane's own back stack instead.",
    );
  }

  return detail === null ? (
    <SectionTwoPaneDepthContext.Provider value={1}>
      <div className={PANES}>
        <div className="lg:w-90 lg:shrink-0" data-testid="two-pane-list">
          {list}
        </div>
        <div
          className="hidden lg:block lg:min-w-0 lg:flex-1"
          data-testid="two-pane-detail"
        >
          {emptyDetail}
        </div>
      </div>
    </SectionTwoPaneDepthContext.Provider>
  ) : (
    <SectionTwoPaneDepthContext.Provider value={1}>
      <div className={PANES}>
        <div
          className="hidden lg:block lg:w-90 lg:shrink-0"
          data-testid="two-pane-list"
        >
          {list}
        </div>
        <div className="lg:min-w-0 lg:flex-1" data-testid="two-pane-detail">
          {detail}
        </div>
      </div>
    </SectionTwoPaneDepthContext.Provider>
  );
};
